"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ModalCallBack } from "@/components/_Modals/ModalCallBack";
import { HeroSection } from "@/components/landing";
import { BeautyCampaignBlocks } from "@/components/landing/BeautyCampaignBlocks";
import { BeautyCampaignModal } from "@/components/landing/BeautyCampaignModal";
import { CompanyEntitySummary } from "@/components/landing/CompanyEntitySummary";
import { useIndustryPreset } from "@/components/landing/IndustryPresetContext";
import { PaymentAcquiring } from "@/components/landing/PaymentAcquiring";
import { HomeUspBlock } from "@/components/landing/HomeUspBlock";
import { NicheOffersBlock } from "@/components/landing/NicheOffersBlock";
import type { CreateNewsDto, GetPortfolioDto } from "@/entities/types/entities";
import { Routes } from "@/shared/constants";
import {
    isCampaignOfferActive,
    type PublicCampaign,
} from "@/shared/campaign/beautyCampaign";
import { useBeautyCampaign } from "@/shared/hooks/useBeautyCampaign";
import { useOnboardModal } from "@/shared/hooks/useOnboardModal";
import type { PublicSubscriptionPlan } from "@/shared/services/publicPlansApi";
import { trackBeautyCampaignEvent } from "@/shared/utils/beautyCampaignAnalytics";
import { MarketingPricingPage } from "@/components/marketing/MarketingPricingPage";
import { CaseBlock } from "@/view/Main/component/CaseBlock";
import { Faq } from "@/view/Main/component/Faq";
import { Form, TelegramChannelBanner } from "@/view/Main/component/Form";
import { HelperBlock } from "@/view/Main/component/HelperBlock";
import { PartnersMarquee } from "@/view/Main/component/PartnersMarquee";
import { TurnkeyLaunchTeaser } from "@/view/TurnkeyLaunch/TurnkeyLaunchTeaser";

import styles from "./Main.module.scss";

type Props = {
    port?: GetPortfolioDto[];
    news?: CreateNewsDto[];
    articles?: CreateNewsDto[];
    plans?: PublicSubscriptionPlan[];
    initialCampaign?: PublicCampaign | null;
};

const MainBody = ({
    port,
    news,
    articles,
    plans,
    initialCampaign,
}: {
    port?: GetPortfolioDto[];
    news?: CreateNewsDto[];
    articles?: CreateNewsDto[];
    plans?: PublicSubscriptionPlan[];
    initialCampaign?: PublicCampaign | null;
}) => {
    const { t } = useTranslation("common");

    const { activePreset, isPlatform } = useIndustryPreset();
    /** Не дергаем /campaigns на платформенной главной — лишние 404 в Network. */
    const beautyMode = !isPlatform && activePreset === "beauty";
    const { campaign, loading } = useBeautyCampaign(initialCampaign, beautyMode);
    const { isModalOpen, leadIntent, openSaasModal, closeModal } = useOnboardModal();
    const [campaignModalOpen, setCampaignModalOpen] = useState(false);
    /** Closed while still on beauty — reopen only after leaving beauty and coming back. */
    const dismissedOnBeautyRef = useRef(false);
    const viewedRef = useRef(false);
    const soldOutViewedRef = useRef(false);
    const modalTrackedRef = useRef(false);

    useEffect(() => {
        if (isPlatform || activePreset !== "beauty") {
            dismissedOnBeautyRef.current = false;
            modalTrackedRef.current = false;
            setCampaignModalOpen(false);
            return;
        }

        if (!viewedRef.current && campaign) {
            viewedRef.current = true;
            trackBeautyCampaignEvent("beauty_campaign_view", campaign);
        }
        if (campaign?.status === "SOLD_OUT" && !soldOutViewedRef.current) {
            soldOutViewedRef.current = true;
            trackBeautyCampaignEvent("beauty_campaign_sold_out_view", campaign);
        }

        if (dismissedOnBeautyRef.current) return;

        const offerLive = isCampaignOfferActive(campaign) || isCampaignOfferActive(initialCampaign);
        if (!offerLive) return;

        setCampaignModalOpen(true);
        if (!modalTrackedRef.current && (campaign || initialCampaign)) {
            modalTrackedRef.current = true;
            trackBeautyCampaignEvent("beauty_campaign_modal_view", campaign ?? initialCampaign);
        }
    }, [activePreset, isPlatform, campaign, initialCampaign]);

    const closeCampaignModal = () => {
        dismissedOnBeautyRef.current = true;
        setCampaignModalOpen(false);
        trackBeautyCampaignEvent("beauty_campaign_modal_close", campaign);
    };

    const claimCampaign = () => {
        dismissedOnBeautyRef.current = true;
        setCampaignModalOpen(false);
        trackBeautyCampaignEvent("beauty_campaign_cta_click", campaign, { source_ui: "modal" });
        trackBeautyCampaignEvent("beauty_campaign_signup_start", campaign, { source_ui: "modal" });
        openSaasModal("start", "Beauty 100", {
            industryPreset: "BEAUTY",
            leadSource: "beauty_100_2026_modal",
            campaignCode: campaign?.campaign || "BEAUTY_100_2026",
        });
    };

    return (
        <div className={styles.page}>
            <CompanyEntitySummary />
            {campaign && (campaign.status === "ACTIVE" || campaign.status === "SCHEDULED") ? (
                <section className={styles.seoOnly} aria-hidden>
                    <h2>BEAUTY 100 · специальная программа</h2>
                    <p>
                        {campaign.maxActivations} Beauty-тенантов бесплатно на {campaign.freeMonths} месяцев.
                        Осталось {campaign.remaining} мест. Mini App, CRM, онлайн-запись, клиенты и сотрудники.
                    </p>
                </section>
            ) : null}
            <HeroSection
                campaign={beautyMode ? campaign : null}
                campaignLoading={beautyMode ? loading : false}
            />
            <HomeUspBlock />
            <NicheOffersBlock />
            {beautyMode ? <BeautyCampaignBlocks campaign={campaign} /> : null}
            <PaymentAcquiring />
            <PartnersMarquee />
            <CaseBlock href={Routes.HOME} listItem={port || []} />
            <MarketingPricingPage plans={plans ?? []} embed showCompare={false} sectionId="tarify" />
            <TurnkeyLaunchTeaser />
            <HelperBlock
                title={t("home.helper.titleNews")}
                ctaLabel={t("home.helper.ctaAllNews")}
                news={news}
                articles={articles}
            />
            <TelegramChannelBanner />
            <Faq campaign={beautyMode ? campaign : null} />
            <Form
                showTelegram={false}
                copy={{
                    eyebrow: t("home.launchForm.eyebrow"),
                    title: t("home.launchForm.title"),
                    description: t("home.launchForm.description"),
                    submitLabel: t("home.launchForm.submitLabel"),
                    successMessage: t("home.launchForm.successMessage"),
                    businessPrefix: t("home.launchForm.businessPrefix"),
                    defaultTariff: t("home.launchForm.defaultTariff"),
                }}
            />
            <BeautyCampaignModal
                open={campaignModalOpen && beautyMode}
                campaign={campaign ?? initialCampaign ?? null}
                loading={loading && !campaign && !initialCampaign}
                onClaim={claimCampaign}
                onBrowse={closeCampaignModal}
                onClose={closeCampaignModal}
            />
            <ModalCallBack isOpen={isModalOpen} onClose={closeModal} leadIntent={leadIntent} />
        </div>
    );
};

export const Main = ({ port, news, articles, plans, initialCampaign }: Props) => {
    return (
        <MainBody
            port={port}
            news={news}
            articles={articles}
            plans={plans}
            initialCampaign={initialCampaign}
        />
    );
};
